import React, { useEffect, useId, useMemo, useRef, useState } from "react";

export default function SearchSelect({
  options = [],               // [{ value: any, label: string }] or arbitrary objects with getters
  value = null,               // selected option's value (primitive) or object id
  onChange,                   // (option) => void
  placeholder = "Select...",
  getOptionLabel = (o) => (o?.label ?? ""),
  getOptionValue = (o) => (o?.value ?? o?.id),
  disabled = false,
  allowClear = true,
  isMulti = false,
  className = "",
  emptyText = "No results",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  // Unique per instance, so several comboboxes on one page don't share a listbox id
  const listboxId = useId();

  // Normalize options to a stable shape
  const norm = useMemo(() => options.map(o => ({
    raw: o,
    value: getOptionValue(o),
    label: String(getOptionLabel(o) ?? "")
  })), [options, getOptionLabel, getOptionValue]);

  const selected = useMemo(() => {
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      return norm.filter(option => values.some(selectedValue => {
        const valueToCompare = selectedValue?.value ?? selectedValue?.id ?? selectedValue;
        return String(option.value) === String(valueToCompare);
      }));
    }
    if (value == null) return null;
    return norm.find(o => String(o.value) === String(value)) || null;
  }, [value, norm, isMulti]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return norm;
    // Simple, fast filter: prefix gets slight priority, then includes
    return norm
      .map((o, idx) => ({ o, idx, pfx: o.label.toLowerCase().startsWith(q) }))
      .sort((a, b) => (b.pfx - a.pfx) || a.idx - b.idx)
      .map(x => x.o)
      .filter(o => o.label.toLowerCase().includes(q));
  }, [norm, query]);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Ensure activeIndex is valid when the list changes
  useEffect(() => {
    if (filtered.length === 0) { setActiveIndex(-1); return; }
    if (activeIndex < 0 || activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered.length]);

  function select(option) {
    if (!option) return;
    if (isMulti) {
      const current = Array.isArray(value) ? value : [];
      const selectedMatch = selected.some(item => String(item.value) === String(option.value));
      onChange?.(selectedMatch
        ? current.filter(selectedValue => String(selectedValue?.value ?? selectedValue?.id ?? selectedValue) !== String(option.value))
        : [...current, option.raw]);
      setQuery("");
      setOpen(true);
    } else {
      onChange?.(option.raw);
      setQuery("");
      setOpen(false);
    }
    // Keep focus on the input for quick changes - at once, not a frame later: by then the user may be in
    // another field, and what they type there would land here
    inputRef.current?.focus();
  }

  function onKeyDown(e) {
    if (!open && (e.key.length === 1 || e.key === "Backspace")) {
      setOpen(true);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.min((i < 0 ? -1 : i) + 1, filtered.length - 1));
      scrollActiveIntoView();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.max((i < 0 ? 1 : i) - 1, 0));
      scrollActiveIntoView();
    } else if (e.key === "Enter") {
      if (open) { e.preventDefault(); select(filtered[activeIndex]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setQuery(""); }
    }
  }

  function scrollActiveIntoView() {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const el = list.querySelector('[data-active="true"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "nearest" });
      }
    });
  }

  return (
    <div ref={rootRef} className={`relative ${disabled ? "opacity-60" : ""} ${className}`}>
      <div className={`flex ${isMulti ? "flex-wrap" : ""} items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500`}>
        {!isMulti && selected && !open && (
          <span className="min-w-0 text-gray-800 dark:text-gray-100 truncate mr-1">
            {selected.label}
          </span>
        )}
        {isMulti && selected.map(option => (
          <span key={option.value} className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
            <span className="max-w-32 truncate">{option.label}</span>
            <button
              type="button"
              onClick={() => onChange?.((Array.isArray(value) ? value : []).filter(selectedValue => String(selectedValue?.value ?? selectedValue?.id ?? selectedValue) !== String(option.value)))}
              className="text-emerald-600 hover:text-red-600 dark:text-emerald-300"
              aria-label={`Remove ${option.label}`}
            >×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={listboxId}
          placeholder={!isMulti && selected && !open ? "" : placeholder}
          value={open ? query : ""}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className="flex-1 min-w-[3rem] bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 outline-none"
        />
        {allowClear && !isMulti && selected && (
          <button
            type="button"
            onClick={() => { onChange?.(null); setQuery(""); setOpen(false); inputRef.current?.focus(); }}
            className="px-1 text-gray-400 hover:text-gray-600"
            aria-label="Clear selection"
          >×</button>
        )}
        <button
          type="button"
          aria-label="Toggle options"
          onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          className="px-1 text-gray-500 hover:text-gray-700"
        >▼</button>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <ul
            id={listboxId}
            role="listbox"
            ref={listRef}
            className="max-h-56 overflow-auto py-1 custom-scrollbar"
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">{emptyText}</li>
            )}
            {filtered.map((o, idx) => {
              const active = idx === activeIndex;
              const selectedMatch = isMulti
                ? selected.some(item => String(o.value) === String(item.value))
                : selected && String(o.value) === String(selected.value);
              return (
                <li
                  key={`${o.value}`}
                  role="option"
                  aria-selected={selectedMatch}
                  data-active={active ? "true" : undefined}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(o)}
                  className={`px-3 py-2 text-sm cursor-pointer select-none flex items-center justify-between ${
                    active ? "bg-emerald-50 dark:bg-emerald-900/30" : ""
                  } ${selectedMatch ? "font-semibold" : ""}`}
                >
                  <span className="truncate">{o.label}</span>
                  {selectedMatch && <span className="ml-2">✓</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}